"use client";

import { Icon, LogoWithText } from "@plura/client/components";
import { Button } from "../../ui/Button";
import { LandingIcon } from "../../atoms/LandingIcon";
import { cn } from "@plura/shared/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import MegaMenu from "./MegaMenu";
import { navigation } from "./navigation";

const Header = () => {
    const [megaMenuIsOpen, setMegaMenuIsOpen] = useState<number | null>(null);
    const [headerIsFloating, setHeaderIsFloating] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
    const pathname = usePathname();

    useEffect(() => {
        const handleScroll = () => setHeaderIsFloating(window.scrollY > 1);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMegaMenuIsOpen(null);
        setShowMobileMenu(false);
    }, [pathname]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1023);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMenuToggle = (index: number) => {
        if (isMobile) {
            setMegaMenuIsOpen(prev => (prev === index ? null : index));
        }
    };

    const navOpen = megaMenuIsOpen !== null || showMobileMenu;

    return (
        <header
            id="header"
            className={cn(
                // Flush full-width bar, no pill/border. White when closed,
                // inverts to dark (white text) when the menu is open.
                'fixed inset-x-0 top-0 z-10 flex h-auto flex-col items-stretch justify-start gap-5',
                navOpen
                    ? cn(
                        'bg-dark text-white',
                        'max-lg:z-40 max-lg:flex max-lg:min-h-0 max-lg:h-[100dvh] max-lg:max-h-[100dvh] max-lg:flex-col max-lg:overflow-hidden',
                        'lg:max-h-[min(560px,90dvh)] lg:overflow-y-auto',
                        'shadow-[0_12px_28px_rgba(0,0,0,0.10)]',
                    )
                    : cn(
                        'bg-white',
                        'max-h-20 overflow-hidden max-lg:max-h-[70px]',
                        headerIsFloating && 'shadow-[0_2px_10px_rgba(0,0,0,0.06)]',
                    ),
                megaMenuIsOpen !== null && 'max-lg:gap-0',
                // Animate bg/text only while opening; closing snaps (no color transition present).
                navOpen
                    ? 'transition-[max-height,box-shadow,background-color,color] duration-300 ease-in-out motion-reduce:transition-none'
                    : 'transition-[max-height,box-shadow] duration-300 ease-in-out motion-reduce:transition-none',
            )}
            onMouseLeave={() => !isMobile && setMegaMenuIsOpen(null)}
        >
            <div
                className={cn(
                    'relative z-0 flex w-full flex-row items-center px-6 py-[15px] max-lg:flex-col max-lg:items-stretch max-lg:justify-start max-lg:px-5 max-lg:py-4',
                    megaMenuIsOpen !== null && 'max-lg:pointer-events-none',
                    megaMenuIsOpen !== null || !navOpen
                        ? 'shrink-0'
                        : 'max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-y-auto',
                )}
            >
                <Link href="/" className="[&_img]:w-[100px]">
                    <LogoWithText darkMode={navOpen} />
                </Link>
                <button
                    type="button"
                    className={cn(
                        'absolute top-3 right-4 hidden min-h-11 min-w-11 cursor-pointer items-center justify-center max-lg:flex',
                        navOpen ? 'text-white transition-colors duration-300' : 'text-t-primary',
                    )}
                    aria-label={showMobileMenu ? "Close mobile menu" : "Open mobile menu"}
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                    {showMobileMenu
                        ? <LandingIcon faClass="fa-regular fa-x" className="!size-8 fill-current" />
                        : <LandingIcon faClass="fa-regular fa-bars" className="!size-8 fill-current" />}
                </button>
                <div className={cn(
                    'flex flex-1 flex-row items-center justify-center gap-8 max-lg:w-full max-lg:flex-1 max-lg:flex-col max-lg:items-stretch max-lg:justify-start max-lg:gap-1 max-lg:py-6',
                    !showMobileMenu && 'max-lg:hidden',
                )}>
                    {navigation.map((item, index) => {
                        if (item.children?.lists) {
                            return (
                                <h6
                                    key={item.title}
                                    className={cn(
                                        'text-link font-body flex cursor-pointer items-center gap-2 max-lg:min-h-12 max-lg:px-1 max-lg:py-3',
                                        navOpen ? 'text-white transition-[color,opacity] duration-300 ease-in-out' : 'text-dark transition-opacity duration-200',
                                        megaMenuIsOpen === index ? 'opacity-60' : 'hover:opacity-60',
                                    )}
                                    onMouseEnter={() => !isMobile && setMegaMenuIsOpen(index)}
                                    onClick={() => handleMenuToggle(index)}
                                >
                                    {item.title}
                                    <Icon
                                        name="chevron"
                                        className={cn(
                                            '!size-5 shrink-0 max-lg:!size-6',
                                            navOpen ? 'fill-white transition-[transform,fill] duration-300' : 'fill-dark transition-transform duration-200',
                                            megaMenuIsOpen === index && 'rotate-180',
                                        )}
                                        aria-hidden
                                    />
                                </h6>
                            );
                        }
                        if (item.link) {
                            return (
                                <Link
                                    href={item.link}
                                    key={item.title}
                                    className="max-lg:block"
                                    onMouseEnter={() => !isMobile && setMegaMenuIsOpen(null)}
                                >
                                    <h6 className={cn(
                                        'text-link font-body cursor-pointer hover:opacity-60 max-lg:min-h-12 max-lg:px-1 max-lg:py-3',
                                        navOpen ? 'text-white transition-[color,opacity] duration-300' : 'text-dark transition-opacity duration-200',
                                    )}>
                                        {item.title}
                                    </h6>
                                </Link>
                            );
                        }
                        return null;
                    })}
                </div>
                <div className={cn(
                    'flex flex-row items-center gap-1 max-lg:w-full max-lg:flex-col max-lg:items-stretch max-lg:justify-start max-lg:gap-2 max-lg:pt-2',
                    !showMobileMenu && 'max-lg:hidden',
                )}>
                    {/*
                       * Landing site is unauthenticated by design — NextAuth
                       * cookies live on app.formsuite.co, so even logged-in
                       * users hit this page with no session. We always render
                       * the public CTAs and let the app handle "already
                       * signed in" routing on the other side.
                       */}
                    <Button
                        variant="ghost"
                        size="medium"
                        href="https://app.formsuite.co/sign-in"
                        className={cn(
                            'max-lg:w-full', navOpen && 'text-white hover:bg-white/10',
                            'font-[18px] font-medium'
                        )}
                    >
                        Log in
                    </Button>
                    <Button
                        variant={navOpen ? "white" : "primary"}
                        size="medium"
                        href="https://app.formsuite.co/sign-up"
                        className="max-lg:w-full"
                    >
                        Create a Free Form
                    </Button>
                </div>
            </div>
            {megaMenuIsOpen !== null && navigation[megaMenuIsOpen] && (
                <div
                    className={cn(
                        'w-full opacity-0 animate-landing-mega-menu',
                        /* Desktop: stack below the bar inside the expanded header */
                        'relative flex min-h-0 flex-1 flex-col overflow-y-auto',
                        /* Mobile: full-screen layer over the nav list (same header box) */
                        'max-lg:absolute max-lg:inset-0 max-lg:z-[2] max-lg:flex max-lg:h-full max-lg:min-h-0 max-lg:flex-none max-lg:flex-col max-lg:overflow-y-auto max-lg:bg-dark',
                    )}
                >
                    <button
                        type="button"
                        className="absolute right-3 top-[max(0.5rem,env(safe-area-inset-top))] z-20 hidden min-h-11 min-w-11 items-center justify-center text-white max-lg:flex"
                        aria-label="Close menu"
                        onClick={() => setMegaMenuIsOpen(null)}
                    >
                        <LandingIcon faClass="fa-regular fa-chevron-left" className="!size-8 fill-current" />
                    </button>
                    <MegaMenu
                        lists={navigation[megaMenuIsOpen].children?.lists ?? []}
                        callout={navigation[megaMenuIsOpen].children?.callout!}
                    />
                </div>
            )}
        </header>
    );
};

export default Header;
